import React, { useState, forwardRef, useImperativeHandle } from 'react';
import './GeneralInfoForm.css'; // Using the same CSS for consistent styling

const Experience = forwardRef(({ formData, setFormData }, ref) => {
     // Add validation support
     const [validationErrors, setValidationErrors] = useState({});

     useImperativeHandle(ref, () => ({
          validateForm: () => {
               return true;
          },
          clearValidation: () => {
               setValidationErrors({});
          }
     }));

     // Initialize with one empty experience row
     const [experiences, setExperiences] = useState(
          formData?.experiences || [
               {
                    previous_company: '',
                    previous_designation: '',
                    previous_experience_years: ''
               }
          ]
     );

     // Add a new experience row
     const addExperienceRow = () => {
          const newExperience = {
               previous_company: '',
               previous_designation: '',
               previous_experience_years: ''
          };
          const updatedExperiences = [...experiences, newExperience];
          setExperiences(updatedExperiences);

          // Update parent form data
          if (setFormData) {
               setFormData(prev => ({
                    ...prev,
                    experiences: updatedExperiences
               }));
          }
     };

     // Remove an experience row
     const removeExperienceRow = (index) => {
          if (experiences.length > 1) {
               const updatedExperiences = experiences.filter((_, i) => i !== index);
               setExperiences(updatedExperiences);

               // Update parent form data
               if (setFormData) {
                    setFormData(prev => ({
                         ...prev,
                         experiences: updatedExperiences
                    }));
               }
          }
     };

     // Handle input changes
     const handleExperienceChange = (index, field, value) => {
          const updatedExperiences = experiences.map((exp, i) =>
               i === index ? { ...exp, [field]: value } : exp
          );
          setExperiences(updatedExperiences);

          // Update parent form data
          if (setFormData) {
               setFormData(prev => ({
                    ...prev,
                    experiences: updatedExperiences
               }));
          }
     };

     return (
          <div className="custom_form_wrapper">


               {/* Table Header */}
               <div className="experience-table-header mb-2">
                    <div className="row gx-2 align-items-center">
                         <div className="col-5">
                              <small className="text-muted fw-semibold">Previous Company</small>
                         </div>
                         <div className="col-3">
                              <small className="text-muted fw-semibold">Previous Designation</small>
                         </div>
                         <div className="col-2">
                              <small className="text-muted fw-semibold">Experience Years</small>
                         </div>
                         <div className="col-1">
                              <small className="text-muted fw-semibold">Action</small>
                         </div>
                         <div className="col-1 text-end">
                              <button
                                   type="button"
                                   className="btn btn-primary btn-sm"
                                   onClick={addExperienceRow}
                                   style={{
                                        fontSize: '0.8rem',
                                        width: '30px',
                                        height: '30px',
                                        minWidth: '30px',
                                        minHeight: '30px',
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                   }}
                              >
                                   <i className="fas fa-plus"></i>
                              </button>
                         </div>
                    </div>
               </div>

               {/* Experience Rows */}
               <div className="experience-table-body">
                    {experiences.map((experience, index) => (
                         <div key={index} className="experience-table-row mb-2">
                              <div className="row gx-2 align-items-center">
                                   {/* Previous Company */}
                                   <div className="col-5">
                                        <div className="form-floating">
                                             <input
                                                  type="text"
                                                  className="form-control form-control-sm"
                                                  id={`previous_company_${index}`}
                                                  placeholder="Previous Company"
                                                  value={experience.previous_company}
                                                  onChange={(e) => handleExperienceChange(index, 'previous_company', e.target.value)}
                                             />
                                             <label htmlFor={`previous_company_${index}`}>Previous Company</label>
                                        </div>
                                   </div>

                                   {/* Previous Designation */}
                                   <div className="col-3">
                                        <div className="form-floating">
                                             <input
                                                  type="text"
                                                  className="form-control form-control-sm"
                                                  id={`previous_designation_${index}`}
                                                  placeholder="Previous Designation"
                                                  value={experience.previous_designation}
                                                  onChange={(e) => handleExperienceChange(index, 'previous_designation', e.target.value)}
                                             />
                                             <label htmlFor={`previous_designation_${index}`}>Previous Designation</label>
                                        </div>
                                   </div>

                                   {/* Previous Experience Years */}
                                   <div className="col-2">
                                        <div className="form-floating">
                                             <input
                                                  type="number"
                                                  className="form-control form-control-sm"
                                                  id={`previous_experience_years_${index}`}
                                                  placeholder="Experience Years"
                                                  value={experience.previous_experience_years}
                                                  onChange={(e) => handleExperienceChange(index, 'previous_experience_years', e.target.value)}
                                                  min="0"
                                                  step="0.1"
                                             />
                                             <label htmlFor={`previous_experience_years_${index}`}>Experience Years</label>
                                        </div>
                                   </div>

                                   {/* Delete Button */}
                                   <div className="col-1 text-center">
                                        {experiences.length > 1 && (
                                             <button
                                                  type="button"
                                                  className="btn btn-outline-danger btn-sm"
                                                  onClick={() => removeExperienceRow(index)}
                                                  style={{
                                                       fontSize: '0.8rem',
                                                       width: '30px',
                                                       height: '30px',
                                                       minWidth: '30px',
                                                       minHeight: '30px',
                                                       padding: 0,
                                                       display: 'flex',
                                                       alignItems: 'center',
                                                       justifyContent: 'center'
                                                  }}
                                                  title="Remove Experience"
                                             >
                                                  <i className="fas fa-trash"></i>
                                             </button>
                                        )}
                                   </div>
                              </div>
                         </div>
                    ))}
               </div>

               {experiences.length === 0 && (
                    <div className="text-center py-4">
                         <p className="text-muted">No experience added yet.</p>
                         <button
                              type="button"
                              className="btn btn-primary"
                              onClick={addExperienceRow}
                         >
                              <i className="fas fa-plus me-2"></i>
                              Add First Experience
                         </button>
                    </div>
               )}
          </div>
     );
});

export default Experience;
